import qs from 'query-string';
import { v4 as uuidv4 } from 'uuid';

import { RESULT_ROUTES_NAMES, ROUTE_NAMES } from '../constants/routes';
import { KEY, ONE_LINK_ID, ORDER_ID } from '../constants/shipping';
import { ICustomizableItem, ICustomizableStoredItem, IFixedAddress, IPickedMskuItem } from '../types/api';
import { ICampaign, ICampaignItem } from '../types/pyg';
import { IBrandingLocationState, ITaxRequiredCountries } from '../types/shell';
import { IInitKeys } from '../types/shipping';

const HASH = '#';
const HEX_REGEX = new RegExp('^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$', 'gm');

// Treats empty strings as missing — backend sends "" in overrides to mean "no override".
export const nullifyEmpty = <T>(value: T | undefined): T | undefined =>
  typeof value === 'string' && value === '' ? undefined : value;

export const parseInitQuery = (initString: string): IInitKeys => {
  if (initString.includes('/o/')) {
    const oneLinkId = initString.split('/o/')[1].split('/')[0];

    return { one_link_id: oneLinkId };
  }
  if (initString.includes('/s/')) {
    const orderId = initString.split('/s/')[1].split('/')[0];
    const key = initString.split('/s/')[1].split('/')[1];

    return { order_id: orderId, key };
  }
  const { order_id, key: parsedKey, one_link_id } = qs.parse(initString) || {};

  return { order_id, key: parsedKey, one_link_id } as IInitKeys;
};

export const validateInitQuery = (keys: IInitKeys | null) =>
  keys && !!((keys[ORDER_ID] && keys[KEY]) || keys[ONE_LINK_ID]);

export const getColor = (value: string | undefined = '', fallback?: string) => {
  let color = fallback ? fallback : undefined;

  const match = value?.match(HEX_REGEX);

  if (match && match?.length) {
    color = value.startsWith(HASH) ? value : `#${value}`;
  }

  return color;
};

export const getStyleVariableValue = (variable: string) => {
  return getComputedStyle(document.documentElement).getPropertyValue(variable)?.trim();
};

export const setBaseColor = (color: string, element: string = 'root') => {
  return document.getElementById(element)?.style.setProperty('--base-color', color);
};

export const setAccentColor = (color: string, element: string = 'root') => {
  return document.getElementById(element)?.style.setProperty('--accent-color', color);
};

export const isMskuItem = ({ sku_options }: ICampaignItem) => !!(sku_options && sku_options.length);

export const isItemOutOfStock = (item: ICampaignItem) =>
  !item.preorder_allowed && (item.count ?? 0) < (item.quantity ?? 0);

export const isMskuOutOfStock = ({ sku_options, quantity }: ICampaignItem): boolean => {
  if (!sku_options?.length || !quantity) {
    return false;
  }

  const totalCount = sku_options.reduce((sum, option) => sum + (option?.count ?? 0), 0);
  const hasPreorderAllowed = sku_options.some((option) => option.preorder_allowed);

  return totalCount < quantity && !hasPreorderAllowed;
};

export const isSkuOptionOutOfStock = (
  { count, preorder_allowed, is_out_of_stock }: ICampaignItem,
  pickedQuantity: number,
  isMarketplace: boolean,
): boolean => {
  if (is_out_of_stock) {
    return true;
  }
  if (preorder_allowed) {
    return false;
  }
  if (isMarketplace) {
    return typeof count === 'number' && (count <= 0 || pickedQuantity >= count);
  }

  return !count || pickedQuantity === count;
};

export const isCampaignBlocked = (campaign: { items?: ICampaignItem[] }) =>
  !!campaign?.items?.some(
    (item) => (!isMskuItem(item) && isItemOutOfStock(item)) || (isMskuItem(item) && isMskuOutOfStock(item)),
  );

export const isCustomizableItem = (item: ICampaignItem) =>
  item.customization && item.customization.item_customization_allowed;

export const isMskuIncluded = ({ items }: ICampaign) => items.some(isMskuItem);

export const isCustomizableItemsIncluded = (items: ICampaignItem[]) => items.some(isCustomizableItem);

export const parseItemsQuantity = (items: ICampaignItem[]) => {
  const mskuItems: ICampaignItem[] = [];

  items.forEach(({ quantity, ...item }) => {
    if (quantity === 1 || !item.sku_options?.length) {
      mskuItems.push({
        ...item,
        internalId: uuidv4(),
        sku_options: item?.sku_options?.map((sku) => ({
          ...sku,
          internalId: uuidv4(),
        })),
        quantity,
      });
    } else {
      for (let i = 0; i < Number(quantity); i++) {
        mskuItems.push({
          ...item,
          sku_options: item.sku_options.map((sku) => ({
            ...sku,
            internalId: uuidv4(),
          })),
          internalId: uuidv4(),
          quantity: 1,
        });
      }
    }
  });

  return mskuItems;
};

export const formatSkuOptions = (items?: ICampaignItem[] | null) => {
  if (typeof items === 'undefined') {
    return null;
  }

  return items ? parseItemsQuantity(items) : [];
};

export const convertInventoryItemsToPickedItems = (items: ICampaignItem[]): { [id: string]: IPickedMskuItem } => {
  return items
    .filter(({ sku_options }) => !!sku_options?.length)
    .reduce((acc, current) => {
      const { internalId } = current;

      return { ...acc, [internalId as string]: null };
    }, {});
};

export const getPickedItemsByDefault = (items: ICampaignItem[]) => {
  return formatSkuOptions(items.filter((item) => !item.sku_options?.length).map((item) => item))?.reduce(
    (acc, current) => {
      const { internalId } = current;

      return {
        ...acc,
        [internalId as string]: {
          quantity: current.quantity,
          uid: current.id,
          parent_id: '',
        },
      };
    },
    {},
  );
};

// this function is used to get the auto picked items for the MSKU step
export const getAutoPickedItems = (items: ICampaignItem[]): { [id: string]: IPickedMskuItem } => {
  return (formatSkuOptions(items) ?? []).reduce((acc, item) => {
    const { internalId, id, quantity, sku_options } = item;
    if (sku_options?.length) {
      const firstAvailable = sku_options.find((o) => (o.count ?? 0) > 0 || o.preorder_allowed) ?? sku_options[0];

      return { ...acc, [internalId as string]: { uid: firstAvailable.id, parent_id: id, quantity } };
    }

    return { ...acc, [internalId as string]: { uid: id, parent_id: '', quantity } };
  }, {});
};

export const preparePickedItemsForAPI = (
  items?: {
    [id: string]: IPickedMskuItem | null;
  } | null,
): { picked_items: IPickedMskuItem[] } | undefined => {
  if (!items) {
    return;
  }

  // tslint:disable-next-line:variable-name
  const picked_items: IPickedMskuItem[] = [];

  Object.values(items).forEach((pickedItem) => {
    const item = picked_items.find((i) => i.uid === pickedItem?.uid && i.parent_id === pickedItem.parent_id);

    if (item) {
      item.quantity += 1;
    } else if (pickedItem) {
      picked_items.push({ ...pickedItem, quantity: 1 });
    }
  });

  return picked_items && picked_items.length ? { picked_items } : undefined;
};

export const getUniqueItemsWithCounts = (items: (IPickedMskuItem | null)[]) => {
  const uniqueItemIds = Array.from(new Set(items.map((item) => item?.uid)));

  return uniqueItemIds.map((itemId) => {
    const itemsArray = items.filter((item) => item?.uid === itemId);
    const quantity = itemsArray.map((item) => (item ? item.quantity : 0)).reduce((prev, curr) => prev + curr, 0);

    return {
      uid: itemId,
      quantity,
    };
  });
};

export const convertInventoryItemsToCustomizableItems = (
  items: ICampaignItem[],
): { [id: string]: ICustomizableStoredItem } => {
  return items.reduce((acc, current) => {
    // tslint:disable-next-line:variable-name
    const { internalId, id: item_id } = current;

    return { ...acc, [internalId as string]: { internalId, item_id } };
  }, {});
};

export const splitInventoryByQuantity = (items: ICampaignItem[]): ICampaignItem[] => {
  const itemsList = items
    .map((item) => {
      const { quantity = 1, ...campaignItem } = item;

      return Array(quantity).fill(campaignItem) as ICampaignItem[];
    })
    .flat();

  return itemsList.map((item) => {
    return {
      ...item,
      internalId: uuidv4(),
    };
  });
};

export const getCustomizableItemsList = (items: ICampaignItem[] | null): ICampaignItem[] => {
  return (
    items?.filter(
      (item: ICampaignItem) => item.customization?.item_customization_allowed && !item.sku_options?.length,
    ) || []
  );
};

export const convertPickedItemsToInventoryItems = (
  pickedItems: { [id: string]: IPickedMskuItem },
  inventoryItems: ICampaignItem[] | null,
): ICampaignItem[] => {
  const result: ICampaignItem[] = [];

  Object.keys(pickedItems).forEach((internalId) => {
    const mskuItem = inventoryItems?.find(
      (multipleOptionItem: ICampaignItem) => multipleOptionItem.internalId === internalId,
    );
    const skuOption = mskuItem?.sku_options?.find((item: ICampaignItem) => item.id === pickedItems[internalId]?.uid);

    if (skuOption) {
      result.push(skuOption);
    }
  });

  return result;
};

export const prepareCustomizedItemsForRequest = (
  items?: {
    [id: string]: ICustomizableStoredItem;
  } | null,
): { custom_items: ICustomizableItem[] } => {
  const customItems = items ? Object.values(items) : [];
  const customizableItemsList = customItems.map(({ internalId, ...item }) => {
    return item;
  });

  return {
    custom_items: customizableItemsList,
  };
};

export const getConvertedPathname = (pathname: string) => {
  return `/${pathname.split('/').at(-1)}` || '';
};

export const isResultRoute = (route: string) => RESULT_ROUTES_NAMES.includes(route);

export const isAuthenticationRoute = (route: string) => route.includes(ROUTE_NAMES.authentication);

export const isMarketplaceRoute = (route: string) => route.includes(ROUTE_NAMES.marketplace);

// The catalog is a detour, not a step: it keeps the branding of the page it was opened from, which
// the navigation carries in location.state.from.
export const resolveBrandingRoute = (pathname: string, state?: IBrandingLocationState | null): string =>
  isMarketplaceRoute(pathname) ? state?.from ?? ROUTE_NAMES.pyg : pathname;

export const chunk = <T>(list: T[], size: number): T[][] => {
  const batches: T[][] = [];

  for (let index = 0; index < list.length; index += size) {
    batches.push(list.slice(index, index + size));
  }

  return batches;
};

export const filterByAllowedCountries = <T>(
  list: T[],
  getCountryCode: (entry: T) => string,
  allowedCountries?: string[],
): T[] => (allowedCountries?.length ? list.filter((entry) => allowedCountries.includes(getCountryCode(entry))) : list);

export const getTaxRequirementInfo = (
  addresses: IFixedAddress[] | undefined,
  addressId: string | undefined,
  taxRequiredCountries: ITaxRequiredCountries | undefined,
) => {
  if (!addresses || !addressId || !taxRequiredCountries) {
    return { isRequired: false };
  }

  const selectedAddress = addresses.find((addr) => addr.uid === addressId);
  if (!selectedAddress?.country) {
    return { isRequired: false };
  }

  const countryCode = selectedAddress.country as keyof ITaxRequiredCountries;
  const taxInfo = taxRequiredCountries[countryCode];

  return {
    isRequired: !!taxInfo,
    taxLabel: taxInfo?.label,
  };
};

export const getPluralForm = (count: number, singular: string, plural: string) => {
  return count === 1 ? singular : plural;
};
